import { useApp } from "@/context/AppContext";
import { useSEO } from "@/hooks/useSEO";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowUpRight } from "lucide-react";
import { KEYWORDS } from "@/lib/seoContent";
import { webPageLd, breadcrumbLd, organizationLd } from "@/lib/structuredData";

const DEV_IMG =
  "https://images.unsplash.com/photo-1587522384446-64daf3e2689a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxjcmVhdGl2ZSUyMGRldmVsb3BlciUyMG1pbmltYWxpc3QlMjBkZXNrfGVufDB8fHx8MTc4MjkzNzUzMHww&ixlib=rb-4.1.0&q=85";

const About = () => {
  const { t, lang } = useApp();
  useSEO({
    title: t.about.metaTitle,
    description: t.about.metaDesc,
    path: "/about",
    lang,
    keywords: KEYWORDS[lang].about,
    jsonLd: [
      organizationLd(),
      webPageLd({
        path: "/about",
        name: t.about.metaTitle,
        description: t.about.metaDesc,
        lang,
        type: "AboutPage",
      }),
      breadcrumbLd([
        { name: t.nav.home, path: "/" },
        { name: t.nav.about, path: "/about" },
      ]),
    ],
  });

  return (
    <main data-testid="about-page" className="pt-16 lg:pt-20">
      <section className="relative">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-16 lg:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">
              <span className="inline-block h-[1px] w-8 bg-[#3A00FF]" />
              {t.about.eyebrow}
            </div>
            <h1
              data-testid="about-title"
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[0.95] tracking-tighter font-black text-[#0A0A0A] font-[Cabinet_Grotesk]"
            >
              {t.about.title}
            </h1>
            <p className="mt-8 max-w-2xl text-base sm:text-lg text-[#525252] leading-relaxed">
              {t.about.lead}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative border border-[#0A0A0A] overflow-hidden shadow-[6px_6px_0px_#3A00FF]">
              <img
                src={DEV_IMG}
                alt="Minimalist developer desk — freelance web designer near me delivering custom website development"
                className="w-full h-full object-cover aspect-[4/5]"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-[#0A0A0A] bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">
              <span className="inline-block h-[1px] w-8 bg-[#3A00FF] align-middle mr-3" />
              01 · Story
            </div>
            <h2 className="mt-4 text-3xl sm:text-4xl leading-tight tracking-tight font-black text-[#0A0A0A] font-[Cabinet_Grotesk]">
              {t.about.storyTitle}
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-6 text-base lg:text-lg text-[#525252] leading-relaxed">
            {t.about.story.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[#0A0A0A] bg-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
          <div className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">
            <span className="inline-block h-[1px] w-8 bg-[#3A00FF]" />
            02 · Principles
          </div>
          <h2 className="mt-4 max-w-3xl text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight font-black text-[#0A0A0A] font-[Cabinet_Grotesk]">
            {t.about.principlesTitle}
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#0A0A0A]/20">
            {t.about.principles.map((p, i) => (
              <div
                key={i}
                data-testid={`principle-card-${i}`}
                className="p-8 border-r border-b border-[#0A0A0A]/20"
              >
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#3A00FF]">
                  0{i + 1}
                </div>
                <h3 className="mt-4 text-xl tracking-tight font-bold font-[Cabinet_Grotesk]">
                  {p.t}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#525252]">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[#0A0A0A] bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter font-black font-[Cabinet_Grotesk]">
              Work with one dedicated partner. Backed by a whole studio.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Link to="/contact">
              <Button
                data-testid="about-cta"
                className="h-14 px-8 rounded-none bg-white text-[#0A0A0A] hover:bg-[#3A00FF] hover:text-white text-xs tracking-widest uppercase font-bold"
              >
                {t.nav.cta}
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
